import { Badge, Box, Divider, Stack, Alert } from "@mui/material";
import { useSSTheme } from "../../reusable/hooks/useSSTheme";
import { SSText } from "src/components/base/components/SSText";
import React from "react";
import { pluralize } from "src/utils/utils";
import { useCssMediaQuery } from "src/components/base/hooks/useCssMediaQuery";
import { SSButton } from "src/components/base/components/SSButton";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";
import { useMarketplaceSeller } from "../marketplace/seller/useMarketplaceSeller";
import { useEventCardCtx } from "./EventCard";
import { EventCardAddOns } from "./EventCardAddOns";
import { EventAttendaceWidget } from "./event-attendance/EventAttendaceWidget";

export const ShowOffersButton = () => {
  const theme = useSSTheme();
  const eventCardCtx = useEventCardCtx();

  return (
    <SSButton
      variant={eventCardCtx?.isDarkBg ? "contained" : "outlined"}
      size="medium"
      colorScheme={eventCardCtx.colors.addOnButtonColorScheme}
      fullWidth
      sx={{
        color: `${theme.colors.common.black} !important`,
      }}
      onClick={() =>
        eventCardCtx.setIsSingleGamesOpen(!eventCardCtx.isSingleGamesOpen)
      }
    >
      Show Offers
      <span
        style={{
          marginLeft: "8px",
          fontSize: "1.4rem",
          lineHeight: 0.8,
          marginTop: eventCardCtx.isAddOnsOpen ? "-3px" : "0px",
        }}
      >
        {eventCardCtx.isSingleGamesOpen ? "-" : "+"}
      </span>
    </SSButton>
  );
};

export const SellTicketsButton = () => {
  const eventCardCtx = useEventCardCtx();
  const { handleBecomeSeller } = useMarketplaceSeller();
  const [isLoading, setIsLoading] = React.useState(false);

  return (
    <SSButton
      loading={isLoading}
      disabled={isLoading}
      variant="contained"
      size="medium"
      colorScheme={eventCardCtx.colors.sellTicketsButtonColorScheme}
      onClick={async () => {
        setIsLoading(true);
        await handleBecomeSeller({
          eventCode: eventCardCtx?.event?.eventCode,
          isListingRedirectWarnEnabled: true,
        });
        setIsLoading(false);
      }}
    >
      Buy or Sell Tickets
    </SSButton>
  );
};

export const AddOnsButton = () => {
  const theme = useSSTheme();
  const eventCardCtx = useEventCardCtx();
  const addOnsTotalSeatCount = eventCardCtx?.addOns?.reduce(
    (acc, itm) => acc + itm.seatCount,
    0
  );
  const { HOME_PAGE_SETTINGS } = useTeamConfig();
  const addOnButtonLabel =
    HOME_PAGE_SETTINGS?.ADD_ON?.ADD_ON_BUTTON_LABEL || "Add Ons";

  return (
    <Badge
      badgeContent={addOnsTotalSeatCount}
      color="primary"
      sx={{
        minWidth: "130px",
        "& .MuiBadge-badge:not(.MuiBadge-invisible)": {
          transform: `scale(1) translate(30%, -40%)`,
        },
      }}
    >
      <SSButton
        variant={eventCardCtx?.isDarkBg ? "contained" : "outlined"}
        size="medium"
        colorScheme={eventCardCtx.colors.addOnButtonColorScheme}
        fullWidth
        sx={{
          color: `${theme.colors.common.black} !important`,
        }}
        onClick={() => eventCardCtx.setIsAddOnsOpen(!eventCardCtx.isAddOnsOpen)}
      >
        {addOnButtonLabel}
        <span
          style={{
            marginLeft: "8px",
            fontSize: "1.4rem",
            lineHeight: 0.8,
            marginTop: eventCardCtx.isAddOnsOpen ? "-3px" : "0px",
          }}
        >
          {eventCardCtx.isAddOnsOpen ? "-" : "+"}
        </span>
      </SSButton>
    </Badge>
  );
};

export function EventCardMarketplaceNotifications() {
  const { md: mdUp } = useCssMediaQuery("up");
  const eventCardCtx = useEventCardCtx();
  const { seatInformation } = eventCardCtx.event;

  const soldCount = seatInformation?.MARKETPLACE_SELL_COUNT || 0;
  const listedCount = seatInformation?.MARKETPLACE_LISTING_COUNT || 0;
  const totalSeatCount = seatInformation?.totalSeatCount || 0;

  // Don't render if both counts are 0
  if (soldCount === 0 && listedCount === 0) {
    return null;
  }

  return (
    <Stack spacing={1.5} px={mdUp ? 3 : 2} pb={2}>
      {soldCount > 0 && (
        <Alert
          severity="info"
          sx={{
            py: 0.5,
            "& .MuiAlert-message": {
              width: "100%",
            },
          }}
        >
          <SSText variant="body" fontSize={"0.9rem"} lineHeight={1.5}>
            <strong>Disclaimer:</strong> You have sold {soldCount}{" "}
            {pluralize("seat", soldCount)} on marketplace. You now have total{" "}
            {totalSeatCount} {pluralize("seat", totalSeatCount)} for this game.
          </SSText>
        </Alert>
      )}

      {listedCount > 0 && (
        <Alert
          severity="warning"
          sx={{
            py: 0.5,
            "& .MuiAlert-message": {
              width: "100%",
            },
          }}
        >
          <SSText variant="body" fontSize={"0.9rem"} lineHeight={1.5}>
            <strong>Note:</strong> You have currently listed {listedCount}{" "}
            {pluralize("seat", listedCount)} on marketplace. If those seats are
            sold, they will be removed from your account.
          </SSText>
        </Alert>
      )}
    </Stack>
  );
}

export function EventCardFooter() {
  const { HOME_PAGE_SETTINGS } = useTeamConfig();
  const { md: mdUp } = useCssMediaQuery("up");
  const eventCardCtx = useEventCardCtx();

  // sell tickets button visibility check
  const isSellTicketsButtonVisible =
    eventCardCtx?.showSellTicketsButton &&
    eventCardCtx?.event?.seatInformation?.totalSeatCount > 0;

  // add ons button visibility check
  const isAddOnsButtonVisible =
    !HOME_PAGE_SETTINGS?.ADD_ON?.OPEN_ADD_ONS_BY_DEFAULT &&
    eventCardCtx?.addOns?.length;

  // attendance widget visibility check
  const isAttendanceWidgetVisible = eventCardCtx?.event?.optInStartDateTime;

  // determine if footer has content
  const isFooterHasContent =
    isAttendanceWidgetVisible ||
    isAddOnsButtonVisible ||
    isSellTicketsButtonVisible;

  return (
    <Box>
      {isFooterHasContent ? (
        <Divider
          sx={{
            backgroundColor: `${eventCardCtx.colors.borderColor} !important`,
            borderColor: "transparent",
          }}
        />
      ) : null}

      <Stack
        width={"100%"}
        p={isFooterHasContent ? (mdUp ? 2 : 1.5) : 0}
        alignItems={"flex-start"}
        direction={mdUp ? "row" : "column"}
        justifyContent={mdUp ? "space-between" : "flex-start"}
        gap={mdUp ? 1 : 1.5}
        flexWrap={"wrap"}
      >
        {/* Attendance Widget - Top on mobile, Left on desktop */}
        <Box width={mdUp ? "auto" : "100%"}>
          {isAttendanceWidgetVisible ? (
            <EventAttendaceWidget
              event={eventCardCtx.event}
              isDarkBg={eventCardCtx.isDarkBg}
            />
          ) : null}
        </Box>

        {/* Action Buttons - Bottom on mobile, Right on desktop */}
        <Stack
          width={mdUp ? "auto" : "100%"}
          direction={mdUp ? "row" : "column"}
          spacing={1}
          justifyContent={mdUp ? "flex-end" : "flex-start"}
          alignItems={"flex-start"}
          sx={{
            // On mobile: full width buttons, one per line
            ...(mdUp
              ? {}
              : {
                  "& > *": {
                    width: "100%",
                    maxWidth: "100%",
                  },
                }),
          }}
        >
          {isSellTicketsButtonVisible ? <SellTicketsButton /> : null}
          {isAddOnsButtonVisible ? <AddOnsButton /> : null}
        </Stack>
      </Stack>

      <EventCardAddOns />
    </Box>
  );
}
